import React, { useState, useEffect } from 'react';
import { getPlayers, getTests } from '../../api';
import { hqRatio, abAdRatio } from '../physicalTestingShared';

const TEST_TYPES = ['sprint', 'cmj', 'isrt', 'weight', 'skinfold', 'passing-drill', 'strength'];

function fmt(v, digits = 1) {
  return v === null || v === undefined || v === '' || isNaN(v) ? '—' : Number(v).toFixed(digits);
}

function buildHistory(data) {
  const byDate = {};
  const ensure = d => { if (!byDate[d]) byDate[d] = { date: d }; return byDate[d]; };
  (data.sprint || []).forEach(r => { ensure(r.test_date?.slice(0, 10)).sprint30m = r.sprint_30m; });
  (data.cmj || []).forEach(r => { ensure(r.test_date?.slice(0, 10)).cmjHeight = r.height_cm; });
  (data.isrt || []).forEach(r => { ensure(r.test_date?.slice(0, 10)).isrtStage = r.final_stage; });
  (data.weight || []).forEach(r => { ensure(r.test_date?.slice(0, 10)).weight = r.weight_kg; });
  (data.skinfold || []).forEach(r => { ensure(r.test_date?.slice(0, 10)).sum8 = r.sum_8; });
  // Passing drill: values are computed by the API; a flagged test prints as "flagged", not a number.
  (data['passing-drill'] || []).forEach(r => {
    const row = ensure(r.test_date?.slice(0, 10));
    if (r.screen_status === 'flagged') row.passingDrillFlagged = true;
    else row.adjPctHRmax = r.adjusted_pct_hrmax;
  });
  (data.strength || []).forEach(r => { const row = ensure(r.test_date?.slice(0, 10)); row.hq = hqRatio(r); row.abad = abAdRatio(r); });
  return Object.values(byDate).sort((a, b) => b.date.localeCompare(a.date));
}

const PRINT_CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  @media print {
    @page { size: A4 landscape; margin: 12mm; }
    body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .no-print { display: none !important; }
  }
  @media screen {
    body { background: #d1d5db; font-family: Inter, Arial, sans-serif; }
    .loading-screen { display: flex; align-items: center; justify-content: center; height: 100vh; font-size: 15px; color: #6b7280; }
    .page { width: 277mm; min-height: 190mm; margin: 24px auto; background: white; box-shadow: 0 4px 24px rgba(0,0,0,0.18); padding: 12mm; box-sizing: border-box; }
  }
  table { width: 100%; border-collapse: collapse; font-family: Inter, Arial, sans-serif; }
  th, td { padding: 6px 10px; font-size: 11px; text-align: left; border-bottom: 1px solid #e5e7eb; color: #111827; }
  th { color: #6b7280; font-weight: 700; text-transform: uppercase; font-size: 9px; }
`;

export default function PhysicalTestingPrint() {
  const [player, setPlayer] = useState(null);
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState('loading');
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    const playerId = localStorage.getItem('print_physical_test_player');
    if (!playerId) { setErrMsg('No player selected. Close this tab and try again.'); setStatus('error'); return; }

    Promise.all([getPlayers(), ...TEST_TYPES.map(t => getTests(t, playerId))])
      .then(([players, ...results]) => {
        const p = (players || []).find(pl => String(pl.id) === String(playerId));
        if (!p) { setErrMsg('Player not found.'); setStatus('error'); return; }
        const data = {};
        TEST_TYPES.forEach((t, i) => { data[t] = results[i] || []; });
        setPlayer(p);
        setHistory(buildHistory(data));
        setStatus('ready');
      })
      .catch(e => { setErrMsg(e.message); setStatus('error'); });
  }, []);

  useEffect(() => {
    if (status !== 'ready') return;
    const t = setTimeout(() => window.print(), 700);
    return () => clearTimeout(t);
  }, [status]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: PRINT_CSS }} />
      {status === 'loading' && <div className="loading-screen no-print">Preparing report…</div>}
      {status === 'error' && <div className="loading-screen no-print" style={{ color: '#ef4444' }}>{errMsg}</div>}
      {status === 'ready' && (
        <section className="page">
          <h1 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{player.name}</h1>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
            {[player.position, player.squad_number ? `#${player.squad_number}` : null].filter(Boolean).join(' · ')} — Physical Test History
          </p>
          <table>
            <thead>
              <tr>
                {['Date', 'Sprint 30m', 'CMJ Height', 'ISRT Stage', 'Weight', 'Sum-8', 'Adj. %HRmax', 'H:Q', 'AB:AD'].map(h => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {history.map(row => (
                <tr key={row.date}>
                  <td style={{ fontWeight: 700 }}>{row.date}</td>
                  <td>{fmt(row.sprint30m, 2)}</td>
                  <td>{fmt(row.cmjHeight)}</td>
                  <td>{fmt(row.isrtStage, 0)}</td>
                  <td>{fmt(row.weight)}</td>
                  <td>{fmt(row.sum8)}</td>
                  <td>{row.passingDrillFlagged ? 'flagged' : fmt(row.adjPctHRmax)}</td>
                  <td>{row.hq != null ? row.hq.toFixed(2) : '—'}</td>
                  <td>{row.abad != null ? `${row.abad.toFixed(0)}%` : '—'}</td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: '#9ca3af', padding: 20 }}>No test history.</td></tr>
              )}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}
